/**
 * Annotation geometry and validation, shared by the portal (where shapes are
 * drawn) and the app (where they are replayed). One rule makes shapes land in
 * the same place on any screen: coordinates are fractions of the VIDEO FRAME,
 * and every renderer first works out where the frame sits inside its view
 * (`fitContain`), then maps through that box. A circle's radius is a fraction
 * of the frame width on both axes, so it is round at every aspect ratio.
 */
import type { Moment, Point, ReviewDraft, Shape, VoiceNote } from "./types";
import type { CoachingConfig } from "./config";

export interface FrameBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

/** Where a frame of `frameW x frameH` sits when letterboxed inside a view. */
export function fitContain(frameW: number, frameH: number, viewW: number, viewH: number): FrameBox {
    if (frameW <= 0 || frameH <= 0 || viewW <= 0 || viewH <= 0) return { x: 0, y: 0, width: 0, height: 0 };
    const scale = Math.min(viewW / frameW, viewH / frameH);
    const width = frameW * scale;
    const height = frameH * scale;
    return { x: (viewW - width) / 2, y: (viewH - height) / 2, width, height };
}

/** A view point (px) to frame space (0..1). Points outside the frame are clamped to its edge. */
export function toFrame(p: Point, box: FrameBox): Point {
    if (box.width <= 0 || box.height <= 0) return { x: 0, y: 0 };
    return {
        x: clamp01((p.x - box.x) / box.width),
        y: clamp01((p.y - box.y) / box.height),
    };
}

/** Frame space (0..1) to a view point (px). */
export function fromFrame(p: Point, box: FrameBox): Point {
    return { x: box.x + p.x * box.width, y: box.y + p.y * box.height };
}

/** A pen length measured in view px, as a fraction of the frame width. */
export function lengthToFrame(px: number, box: FrameBox): number {
    return box.width > 0 ? px / box.width : 0;
}

export function clamp01(v: number): number {
    return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Pixel primitives a renderer can draw without knowing the shape model. */
export type PixelShape =
    | { kind: "path"; points: Point[]; color: string; strokeWidth: number }
    | { kind: "line"; from: Point; to: Point; color: string; strokeWidth: number }
    | { kind: "arrow"; from: Point; to: Point; head: [Point, Point, Point]; color: string; strokeWidth: number }
    | { kind: "circle"; centre: Point; radius: number; color: string; strokeWidth: number }
    | { kind: "rect"; origin: Point; width: number; height: number; color: string; strokeWidth: number };

/** Stroke widths are stored in frame-width units too, so lines scale with the video. */
export function strokePx(width: number, box: FrameBox): number {
    return Math.max(1, width * box.width);
}

function arrowHead(from: Point, to: Point, strokeWidth: number): [Point, Point, Point] {
    const a = Math.atan2(to.y - from.y, to.x - from.x);
    const length = Math.max(10, strokeWidth * 4.5);
    const spread = 0.42;
    return [
        { x: to.x - length * Math.cos(a - spread), y: to.y - length * Math.sin(a - spread) },
        to,
        { x: to.x - length * Math.cos(a + spread), y: to.y - length * Math.sin(a + spread) },
    ];
}

/** Project one shape into a frame box. Rects drawn backwards are normalised. */
export function toPixels(shape: Shape, box: FrameBox): PixelShape {
    const strokeWidth = strokePx(shape.width, box);
    switch (shape.kind) {
    case "path":
        return { kind: "path", points: shape.points.map((p) => fromFrame(p, box)), color: shape.color, strokeWidth };
    case "line":
        return {
            kind: "line",
            from: fromFrame({ x: shape.x1, y: shape.y1 }, box),
            to: fromFrame({ x: shape.x2, y: shape.y2 }, box),
            color: shape.color,
            strokeWidth,
        };
    case "arrow": {
        const from = fromFrame({ x: shape.x1, y: shape.y1 }, box);
        const to = fromFrame({ x: shape.x2, y: shape.y2 }, box);
        return { kind: "arrow", from, to, head: arrowHead(from, to, strokeWidth), color: shape.color, strokeWidth };
    }
    case "circle":
        return {
            kind: "circle",
            centre: fromFrame({ x: shape.cx, y: shape.cy }, box),
            radius: Math.max(1, shape.r * box.width),
            color: shape.color,
            strokeWidth,
        };
    case "rect": {
        const x = shape.w < 0 ? shape.x + shape.w : shape.x;
        const y = shape.h < 0 ? shape.y + shape.h : shape.y;
        const origin = fromFrame({ x, y }, box);
        return {
            kind: "rect",
            origin,
            width: Math.max(1, Math.abs(shape.w) * box.width),
            height: Math.max(1, Math.abs(shape.h) * box.height),
            color: shape.color,
            strokeWidth,
        };
    }
    }
}

export const ANNOTATION_COLOURS = ["#00e5a0", "#3d8bff", "#ffc53d", "#ff4d4d", "#ffffff"] as const;
export const DEFAULT_STROKE = 0.004;
const MIN_STROKE = 0.001;
const MAX_STROKE = 0.03;
export const DEFAULT_HOLD_SEC = 4;
const MAX_HOLD_SEC = 30;
const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const COLOUR_RE = /^#[0-9a-fA-F]{6}$/;

export type ParseFailure = { ok: false; path: string; reason: string };
export type ParseResult<T> = { ok: true; value: T } | ParseFailure;

const fail = (path: string, reason: string): ParseFailure => ({ ok: false, path, reason });

function unit(v: unknown): v is number {
    return typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 1;
}

function stroke(v: unknown): number | null {
    if (typeof v !== "number" || !Number.isFinite(v)) return null;
    return Math.min(MAX_STROKE, Math.max(MIN_STROKE, v));
}

function colour(v: unknown): string | null {
    return typeof v === "string" && COLOUR_RE.test(v) ? v.toLowerCase() : null;
}

/** Check one shape from an untrusted client. Numbers are clamped to the frame, never trusted. */
export function parseShape(raw: unknown, path: string, cfg: CoachingConfig): ParseResult<Shape> {
    if (!raw || typeof raw !== "object") return fail(path, "not an object");
    const s = raw as Record<string, unknown>;
    const color = colour(s.color);
    if (!color) return fail(`${path}.color`, "not a hex colour");
    const width = stroke(s.width);
    if (width === null) return fail(`${path}.width`, "not a stroke width");
    if (s.at !== undefined && (typeof s.at !== "number" || !Number.isFinite(s.at) || s.at < 0 || s.at > cfg.maxVoiceSec + 1)) {
        return fail(`${path}.at`, "not a time in the voice note");
    }
    const timing = typeof s.at === "number" ? { at: Math.round(s.at * 100) / 100 } : {};
    switch (s.kind) {
    case "path": {
        if (!Array.isArray(s.points)) return fail(`${path}.points`, "not a list");
        if (s.points.length < 1) return fail(`${path}.points`, "empty");
        if (s.points.length > cfg.maxPointsPerPath) return fail(`${path}.points`, "too many points");
        const points: Point[] = [];
        for (let i = 0; i < s.points.length; i++) {
            const p = s.points[i] as Record<string, unknown> | null;
            if (!p || !unit(p.x) || !unit(p.y)) return fail(`${path}.points[${i}]`, "not a frame point");
            points.push({ x: p.x, y: p.y });
        }
        return { ok: true, value: { kind: "path", points, color, width, ...timing } };
    }
    case "line":
    case "arrow": {
        if (!unit(s.x1) || !unit(s.y1) || !unit(s.x2) || !unit(s.y2)) return fail(path, "endpoints outside the frame");
        return { ok: true, value: { kind: s.kind, x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2, color, width, ...timing } };
    }
    case "circle": {
        if (!unit(s.cx) || !unit(s.cy)) return fail(path, "centre outside the frame");
        if (typeof s.r !== "number" || !Number.isFinite(s.r) || s.r <= 0 || s.r > 1) return fail(`${path}.r`, "not a radius");
        return { ok: true, value: { kind: "circle", cx: s.cx, cy: s.cy, r: s.r, color, width, ...timing } };
    }
    case "rect": {
        if (!unit(s.x) || !unit(s.y)) return fail(path, "origin outside the frame");
        if (typeof s.w !== "number" || typeof s.h !== "number" || !Number.isFinite(s.w) || !Number.isFinite(s.h)) {
            return fail(path, "size not finite");
        }
        if (Math.abs(s.w) > 1 || Math.abs(s.h) > 1) return fail(path, "size larger than the frame");
        return { ok: true, value: { kind: "rect", x: s.x, y: s.y, w: s.w, h: s.h, color, width, ...timing } };
    }
    default:
        return fail(`${path}.kind`, "unknown shape");
    }
}

/** The clips a review may place moments on, in bucket order, with their lengths. */
export interface ClipLength {
    id: string;
    durationSec: number;
}

/** What the server names a voice file: 16 hex characters and `.m4a`. Nothing a client invents passes. */
export const VOICE_KEY_RE = /^[a-f0-9]{16}\.m4a$/;

/** A voice note on a draft: absent or null means none; anything else must be a key the server made. */
export function parseVoice(raw: unknown, path: string, cfg: Pick<CoachingConfig, "maxVoiceSec">): ParseResult<VoiceNote | null> {
    if (raw === undefined || raw === null) return { ok: true, value: null };
    if (typeof raw !== "object") return fail(path, "not a voice note");
    const v = raw as Record<string, unknown>;
    if (typeof v.key !== "string" || !VOICE_KEY_RE.test(v.key)) return fail(`${path}.key`, "not a voice file");
    if (typeof v.durationSec !== "number" || !Number.isFinite(v.durationSec) || v.durationSec <= 0 || v.durationSec > cfg.maxVoiceSec + 1) {
        return fail(`${path}.durationSec`, "not a voice length");
    }
    return { ok: true, value: { key: v.key, durationSec: Math.round(v.durationSec * 10) / 10 } };
}

export function parseMoment(raw: unknown, path: string, clips: readonly ClipLength[], cfg: CoachingConfig): ParseResult<Moment> {
    if (!raw || typeof raw !== "object") return fail(path, "not an object");
    const m = raw as Record<string, unknown>;
    if (typeof m.id !== "string" || !ID_RE.test(m.id)) return fail(`${path}.id`, "not an id");
    const clip = clips.find((c) => c.id === m.clipId);
    if (!clip) return fail(`${path}.clipId`, "not a clip of this submission");
    if (typeof m.t !== "number" || !Number.isFinite(m.t) || m.t < 0) return fail(`${path}.t`, "not a time");
    if (m.t > clip.durationSec + 0.5) return fail(`${path}.t`, "past the end of the clip");
    const holdSec = typeof m.holdSec === "number" && Number.isFinite(m.holdSec)
        ? Math.min(MAX_HOLD_SEC, Math.max(0, m.holdSec))
        : DEFAULT_HOLD_SEC;
    const label = typeof m.label === "string" ? m.label.trim().slice(0, 80) : "";
    if (typeof m.note !== "string") return fail(`${path}.note`, "not text");
    if (m.note.length > cfg.maxMomentNoteChars) return fail(`${path}.note`, "too long");
    if (!Array.isArray(m.shapes)) return fail(`${path}.shapes`, "not a list");
    if (m.shapes.length > cfg.maxShapesPerMoment) return fail(`${path}.shapes`, "too many shapes");
    const shapes: Shape[] = [];
    for (let i = 0; i < m.shapes.length; i++) {
        const r = parseShape(m.shapes[i], `${path}.shapes[${i}]`, cfg);
        if (r.ok === false) return fail(r.path, r.reason);
        shapes.push(r.value);
    }
    const order = typeof m.order === "number" && Number.isFinite(m.order) ? Math.round(m.order) : 0;
    const voice = parseVoice(m.voice, `${path}.voice`, cfg);
    if (voice.ok === false) return fail(voice.path, voice.reason);
    return { ok: true, value: { id: m.id, clipId: clip.id, t: Math.round(m.t * 1000) / 1000, holdSec, label, note: m.note.trim(), shapes, order, voice: voice.value } };
}

/**
 * Check a whole draft save. Moments come back in bucket order (clip order,
 * then time) with distinct ids, which is the order both viewers walk them.
 */
export function parseReviewDraft(raw: unknown, clips: readonly ClipLength[], cfg: CoachingConfig): ParseResult<ReviewDraft> {
    if (!raw || typeof raw !== "object") return fail("draft", "not an object");
    const d = raw as Record<string, unknown>;
    if (typeof d.summary !== "string") return fail("draft.summary", "not text");
    if (d.summary.length > cfg.maxSummaryChars) return fail("draft.summary", "too long");
    if (typeof d.baseRevision !== "number" || !Number.isInteger(d.baseRevision) || d.baseRevision < 0) {
        return fail("draft.baseRevision", "not a revision");
    }
    const summaryVoice = parseVoice(d.summaryVoice, "draft.summaryVoice", cfg);
    if (summaryVoice.ok === false) return fail(summaryVoice.path, summaryVoice.reason);
    if (!Array.isArray(d.moments)) return fail("draft.moments", "not a list");
    if (d.moments.length > cfg.maxMomentsPerReview) return fail("draft.moments", "too many moments");
    const moments: Moment[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < d.moments.length; i++) {
        const r = parseMoment(d.moments[i], `draft.moments[${i}]`, clips, cfg);
        if (r.ok === false) return fail(r.path, r.reason);
        if (seen.has(r.value.id)) return fail(`draft.moments[${i}].id`, "duplicate id");
        seen.add(r.value.id);
        moments.push(r.value);
    }
    const clipIndex = new Map(clips.map((c, i) => [c.id, i]));
    moments.sort((a, b) => (clipIndex.get(a.clipId) ?? 0) - (clipIndex.get(b.clipId) ?? 0) || a.t - b.t || a.order - b.order);
    moments.forEach((m, i) => { m.order = i; });
    return { ok: true, value: { summary: d.summary.trim(), summaryVoice: summaryVoice.value, moments, baseRevision: d.baseRevision } };
}

/** A review can go back once it says something: a shape, a note, a voice note or a summary. */
export function reviewHasContent(summary: string, moments: readonly Moment[], summaryVoice: VoiceNote | null = null): boolean {
    if (summary.trim().length > 0 || summaryVoice) return true;
    return moments.some((m) => m.shapes.length > 0 || m.note.trim().length > 0 || !!m.voice);
}

/** The drawings to show `voiceSec` into a moment's voice note: those drawn by then, and any never timed. */
export function shapesAt(shapes: readonly Shape[], voiceSec: number | null): Shape[] {
    if (voiceSec === null) return [...shapes];
    return shapes.filter((s) => s.at === undefined || s.at <= voiceSec);
}

/** How long a moment holds its frame: its own hold, or longer when its voice note runs on. */
export function holdFor(m: Pick<Moment, "holdSec" | "voice">): number {
    return Math.max(m.holdSec, m.voice ? m.voice.durationSec + 0.5 : 0);
}

/** The moment of `clipId` whose frame should be showing at `t` seconds into that clip, if any. */
export function momentAt(moments: readonly Moment[], clipId: string, t: number): Moment | null {
    let best: Moment | null = null;
    for (const m of moments) {
        if (m.clipId !== clipId) continue;
        if (m.t <= t + 0.05 && t < m.t + Math.max(m.holdSec, 0.5)) {
            if (!best || m.t > best.t) best = m;
        }
    }
    return best;
}
